import React, { useMemo, useState } from 'react';
import styled from 'styled-components';

import { useServiceCatalog } from './ServiceCatalogContext';
import ProductCard from './ProductCard';

const ServicesPageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 3rem 2rem;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 1rem;
`;

const PageIntro = styled.p`
  font-size: 1.1rem;
  text-align: center;
  color: #555;
  margin-bottom: 2.5rem;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
`;

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.75rem;
  margin-bottom: 2.5rem;
`;

const FilterButton = styled.button`
  padding: 0.6rem 1.25rem;
  border-radius: 25px;
  border: 1px solid ${({ $active }) => ($active ? '#005eb8' : '#e0e0e0')};
  background-color: ${({ $active }) => ($active ? '#005eb8' : '#fff')};
  color: ${({ $active }) => ($active ? '#fff' : '#333')};
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s, border-color 0.2s, color 0.2s;

  &:hover {
    border-color: #005eb8;
    ${({ $active }) => !$active && 'color: #005eb8;'}
  }
`;

const ResultsCount = styled.p`
  text-align: center;
  color: #777;
  font-size: 0.95rem;
  margin-bottom: 1.5rem;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
`;

const NoResults = styled.p`
  text-align: center;
  color: #777;
  font-size: 1.05rem;
  padding: 3rem 0;
`;

const Services = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const { allServices, serviceCategories } = useServiceCatalog();

  const productCategoryNames = useMemo(() => (
    serviceCategories.filter((category) => category.id !== 'all-services').map((category) => category.name)
  ), [serviceCategories]);

  const filteredServices = useMemo(() => {
    if (activeCategory === 'All') return allServices;
    return allServices.filter((service) => service.category === activeCategory);
  }, [activeCategory, allServices]);

  return (
    <ServicesPageContainer>
      <PageTitle>All Services</PageTitle>
      <PageIntro>
        Browse our full range of professional letter writing services. Each service is handled by
        our experienced, UK-based doctors to ensure the highest quality and authenticity.
      </PageIntro>

      <FilterBar>
        <FilterButton
          type="button"
          $active={activeCategory === 'All'}
          onClick={() => setActiveCategory('All')}
        >
          All
        </FilterButton>
        {productCategoryNames.map((name) => (
          <FilterButton
            key={name}
            type="button"
            $active={activeCategory === name}
            onClick={() => setActiveCategory(name)}
          >
            {name}
          </FilterButton>
        ))}
      </FilterBar>

      <ResultsCount>
        {filteredServices.length} service{filteredServices.length === 1 ? '' : 's'}
      </ResultsCount>

      {filteredServices.length > 0 ? (
        <ServicesGrid>
          {filteredServices.map((service) => (
            <ProductCard key={service.id} service={service} />
          ))}
        </ServicesGrid>
      ) : (
        <NoResults>No services found in this category yet.</NoResults>
      )}
    </ServicesPageContainer>
  );
};

export default Services;
